import { STORAGE_KEYS } from '@/config/constants';

type SfxKey = 'correct' | 'wrong' | 'tick' | 'gameover' | 'levelup';

interface Tone {
  freq: number;
  duration: number;
  type: OscillatorType;
  gain: number;
  freqEnd?: number;
}

const presets: Record<SfxKey, Tone[]> = {
  correct: [
    { freq: 660, duration: 0.08, type: 'triangle', gain: 0.25 },
    { freq: 990, duration: 0.12, type: 'triangle', gain: 0.25 }
  ],
  wrong: [
    { freq: 220, duration: 0.18, type: 'sawtooth', gain: 0.22, freqEnd: 110 }
  ],
  tick: [{ freq: 1200, duration: 0.04, type: 'square', gain: 0.1 }],
  gameover: [
    { freq: 440, duration: 0.18, type: 'sawtooth', gain: 0.22, freqEnd: 220 },
    { freq: 220, duration: 0.28, type: 'sawtooth', gain: 0.22, freqEnd: 80 }
  ],
  levelup: [
    { freq: 523, duration: 0.08, type: 'triangle', gain: 0.22 },
    { freq: 659, duration: 0.08, type: 'triangle', gain: 0.22 },
    { freq: 784, duration: 0.12, type: 'triangle', gain: 0.22 }
  ]
};

let ctx: AudioContext | null = null;
let muted = false;
let initialized = false;

// === Música ambiental ===
// Pad de acordes lentos con osciladores. Se solapan para transición suave.
const PAD_CHORDS: number[][] = [
  [146.83, 220.00, 293.66], // Dm5 (D3 A3 D4)
  [130.81, 196.00, 261.63], // Csus (C3 G3 C4)
  [110.0, 164.81, 220.0], // A5 (A2 E3 A3)
  [123.47, 185.0, 246.94] // B5 (B2 F#3 B3)
];
const PAD_ATTACK = 2.0;
const PAD_SUSTAIN = 3.0;
const PAD_RELEASE = 2.5;
const PAD_LEN = PAD_ATTACK + PAD_SUSTAIN + PAD_RELEASE; // 7.5s
const PAD_NEXT_OFFSET = PAD_ATTACK + PAD_SUSTAIN; // 5s — el próximo acorde entra antes de que el actual termine

let musicMaster: GainNode | null = null;
let musicFilter: BiquadFilterNode | null = null;
let musicNextTimer: number | null = null;
let musicChordIdx = 0;
let musicEnabled = false; // pedida por la escena
let musicPlaying = false;
const listeners: Array<(muted: boolean) => void> = [];

function ensureMusicChain(c: AudioContext): void {
  if (musicMaster && musicFilter) return;
  musicFilter = c.createBiquadFilter();
  musicFilter.type = 'lowpass';
  musicFilter.frequency.value = 1400;
  musicFilter.Q.value = 0.5;
  musicMaster = c.createGain();
  musicMaster.gain.value = 0.45;
  musicFilter.connect(musicMaster).connect(c.destination);
}

function scheduleNextPad(): void {
  if (!musicPlaying) return;
  const c = ctx;
  if (!c || !musicFilter) return;
  const chord = PAD_CHORDS[musicChordIdx];
  musicChordIdx = (musicChordIdx + 1) % PAD_CHORDS.length;

  const t0 = c.currentTime;
  for (const freq of chord) {
    for (const detune of [-6, 6]) {
      const osc = c.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.detune.value = detune;
      const g = c.createGain();
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(0.06, t0 + PAD_ATTACK);
      g.gain.setValueAtTime(0.06, t0 + PAD_ATTACK + PAD_SUSTAIN);
      g.gain.linearRampToValueAtTime(0, t0 + PAD_LEN);
      osc.connect(g).connect(musicFilter);
      osc.start(t0);
      osc.stop(t0 + PAD_LEN + 0.2);
    }
  }

  musicNextTimer = window.setTimeout(scheduleNextPad, PAD_NEXT_OFFSET * 1000);
}

function startMusicInternal(): void {
  if (muted || musicPlaying) return;
  const c = ensureCtx();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();
  ensureMusicChain(c);
  if (musicMaster) {
    musicMaster.gain.cancelScheduledValues(c.currentTime);
    musicMaster.gain.linearRampToValueAtTime(0.45, c.currentTime + 0.6);
  }
  musicPlaying = true;
  scheduleNextPad();
}

function stopMusicInternal(): void {
  if (!musicPlaying) return;
  musicPlaying = false;
  if (musicNextTimer !== null) {
    clearTimeout(musicNextTimer);
    musicNextTimer = null;
  }
  const c = ctx;
  if (c && musicMaster) {
    musicMaster.gain.cancelScheduledValues(c.currentTime);
    musicMaster.gain.linearRampToValueAtTime(0, c.currentTime + 0.5);
  }
}

function ensureCtx(): AudioContext | null {
  if (ctx) return ctx;
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  return ctx;
}

function playTone(tone: Tone, startAt: number): number {
  const c = ensureCtx();
  if (!c || muted) return startAt;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = tone.type;
  osc.frequency.setValueAtTime(tone.freq, startAt);
  if (tone.freqEnd) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, tone.freqEnd), startAt + tone.duration);
  }
  g.gain.setValueAtTime(0, startAt);
  g.gain.linearRampToValueAtTime(tone.gain, startAt + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, startAt + tone.duration);
  osc.connect(g).connect(c.destination);
  osc.start(startAt);
  osc.stop(startAt + tone.duration + 0.02);
  return startAt + tone.duration * 0.85;
}

export const audioService = {
  init() {
    if (initialized) return;
    initialized = true;
    muted = localStorage.getItem(STORAGE_KEYS.muted) === '1';

    const unlock = () => {
      const c = ensureCtx();
      c?.resume?.();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchend', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('touchend', unlock, { once: true });
  },

  play(key: SfxKey) {
    const c = ensureCtx();
    if (!c || muted) return;
    if (c.state === 'suspended') c.resume();
    let t = c.currentTime + 0.001;
    for (const tone of presets[key]) {
      t = playTone(tone, t);
    }
  },

  setMuted(value: boolean) {
    muted = value;
    localStorage.setItem(STORAGE_KEYS.muted, muted ? '1' : '0');
    if (muted) {
      stopMusicInternal();
    } else if (musicEnabled) {
      startMusicInternal();
    }
    listeners.forEach((l) => l(muted));
  },

  isMuted(): boolean {
    return muted;
  },

  toggleMute(): boolean {
    this.setMuted(!muted);
    return muted;
  },

  /** Llamado por la escena cuando entra en juego. */
  enableMusic() {
    musicEnabled = true;
    if (!muted) startMusicInternal();
  },

  /** Llamado por la escena cuando sale del juego. */
  disableMusic() {
    musicEnabled = false;
    stopMusicInternal();
  },

  /** Suscribirse a cambios de mute (para refrescar la UI del HUD). */
  onMuteChange(cb: (muted: boolean) => void): () => void {
    listeners.push(cb);
    return () => {
      const i = listeners.indexOf(cb);
      if (i >= 0) listeners.splice(i, 1);
    };
  }
};
